import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import './ImageZoomModal.css';

/**
 * Модальное окно для просмотра изображений с зумом
 */
function ImageZoomModal({ images, initialIndex = 0, onClose }) {
  const { t } = useTranslation();
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [zoom, setZoom] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

  const currentImage = images[currentIndex];

  // Сброс зума при смене изображения
  useEffect(() => {
    setZoom(1);
    setPosition({ x: 0, y: 0 });
  }, [currentIndex]);

  // Обработка клавиш
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') handlePrev();
      if (e.key === 'ArrowRight') handleNext();
      if (e.key === '+' || e.key === '=') handleZoomIn();
      if (e.key === '-') handleZoomOut();
      if (e.key === '0') handleZoomReset();
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentIndex, zoom]);

  const handlePrev = () => {
    setCurrentIndex((i) => (i === 0 ? images.length - 1 : i - 1));
  };

  const handleNext = () => {
    setCurrentIndex((i) => (i === images.length - 1 ? 0 : i + 1));
  };

  const handleZoomIn = () => {
    setZoom((z) => Math.min(z + 0.5, 5));
  };

  const handleZoomOut = () => {
    setZoom((z) => Math.max(z - 0.5, 1));
  };

  const handleZoomReset = () => {
    setZoom(1);
    setPosition({ x: 0, y: 0 });
  };

  const handleWheel = (e) => {
    e.preventDefault();
    if (e.deltaY < 0) {
      handleZoomIn();
    } else {
      handleZoomOut();
    }
  };

  const handleMouseDown = (e) => {
    if (zoom > 1) {
      setIsDragging(true);
      setDragStart({ x: e.clientX - position.x, y: e.clientY - position.y });
    }
  };

  const handleMouseMove = (e) => {
    if (isDragging && zoom > 1) {
      setPosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y,
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleTouchStart = (e) => {
    if (e.touches.length === 2) {
      // Пинч-зум
      const distance = Math.hypot(
        e.touches[0].clientX - e.touches[1].clientX,
        e.touches[0].clientY - e.touches[1].clientY
      );
      setDragStart({ ...dragStart, pinchDistance: distance });
    } else if (e.touches.length === 1 && zoom > 1) {
      setIsDragging(true);
      setDragStart({ x: e.touches[0].clientX - position.x, y: e.touches[0].clientY - position.y });
    }
  };

  const handleTouchMove = (e) => {
    e.preventDefault();
    
    if (e.touches.length === 2 && dragStart.pinchDistance) {
      // Пинч-зум
      const distance = Math.hypot(
        e.touches[0].clientX - e.touches[1].clientX,
        e.touches[0].clientY - e.touches[1].clientY
      );
      const scale = distance / dragStart.pinchDistance;
      setZoom((z) => Math.max(1, Math.min(5, z * scale)));
    } else if (e.touches.length === 1 && isDragging) {
      setPosition({
        x: e.touches[0].clientX - dragStart.x,
        y: e.touches[0].clientY - dragStart.y,
      });
    }
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
    setDragStart({ ...dragStart, pinchDistance: null });
  };

  return (
    <div className="imageZoomModal" onClick={onClose}>
      <div className="imageZoomModalContent" onClick={(e) => e.stopPropagation()}>
        {/* Кнопка закрытия */}
        <button className="imageZoomModalClose" onClick={onClose} aria-label={t('common.close')}>
          ×
        </button>

        {/* Навигация */}
        {images.length > 1 && (
          <>
            <button
              className="imageZoomModalNav imageZoomModalNavPrev"
              onClick={handlePrev}
              aria-label={t('common.previous')}
            >
              ‹
            </button>
            <button
              className="imageZoomModalNav imageZoomModalNavNext"
              onClick={handleNext}
              aria-label={t('common.next')}
            >
              ›
            </button>
          </>
        )}

        {/* Контейнер изображения */}
        <div
          className="imageZoomModalImageContainer"
          onWheel={handleWheel}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          style={{ cursor: zoom > 1 ? (isDragging ? 'grabbing' : 'grab') : 'default' }}
        >
          <img
            src={currentImage}
            alt={`Image ${currentIndex + 1} of ${images.length}`}
            className="imageZoomModalImage"
            style={{
              transform: `translate(${position.x}px, ${position.y}px) scale(${zoom})`,
              transition: isDragging ? 'none' : 'transform 0.2s ease-out',
            }}
            draggable={false}
          />
        </div>

        {/* Контролы зума */}
        <div className="imageZoomModalControls">
          <button
            className="imageZoomModalControlButton"
            onClick={handleZoomOut}
            disabled={zoom <= 1}
            aria-label={t('common.zoomOut')}
          >
            −
          </button>
          <span className="imageZoomModalZoomLevel">{Math.round(zoom * 100)}%</span>
          <button
            className="imageZoomModalControlButton"
            onClick={handleZoomIn}
            disabled={zoom >= 5}
            aria-label={t('common.zoomIn')}
          >
            +
          </button>
          <button
            className="imageZoomModalControlButton"
            onClick={handleZoomReset}
            disabled={zoom === 1}
            aria-label={t('common.resetZoom')}
          >
            ↺
          </button>
        </div>

        {/* Индикатор изображения */}
        {images.length > 1 && (
          <div className="imageZoomModalIndicator">
            {currentIndex + 1} / {images.length}
          </div>
        )}

        {/* Миниатюры */}
        {images.length > 1 && (
          <div className="imageZoomModalThumbnails">
            {images.map((img, i) => (
              <button
                key={i}
                className={`imageZoomModalThumbnail ${i === currentIndex ? 'active' : ''}`}
                onClick={() => setCurrentIndex(i)}
                aria-label={`View image ${i + 1}`}
              >
                <img src={img} alt={`Thumbnail ${i + 1}`} />
              </button>
            ))}
          </div>
        )}

        {/* Подсказки */}
        <div className="imageZoomModalHints">
          <span>{t('common.zoomHints')}</span>
        </div>
      </div>
    </div>
  );
}

export default ImageZoomModal;